import { lazy } from 'react';
import {
  IconBuildingBank,
  IconCertificate,
  IconFileInvoice,
  IconReceiptTax,
  IconReportAnalytics,
  IconScale,
  IconShoppingCart,
  IconSettings,
} from '@tabler/icons-react';
import { defineModule } from '../types';
import { indexRoute, lazyRoute, routeGroup } from '../routing';
import { P } from './perms';

const widgets = () => import('./widgets');
const EInvoiceStatusWidget = lazy(() => widgets().then((m) => ({ default: m.EInvoiceStatusWidget })));

const einv = () => import('./pages/EInvoicePages');
const egs = () => import('./pages/EgsPages');
const returns = () => import('./pages/ReturnPages');
const misc = () => import('./pages/TaxPages');
const zakat = () => import('./pages/ZakatPages');

/**
 * TAX — VAT, ZATCA e-invoicing phase 2 (Fatoora), zakat.
 */
export default defineModule({
  code: 'tax',
  nameAr: 'الضرائب والزكاة',
  nameEn: 'Tax & Zakat',
  icon: IconReceiptTax,
  order: 110,
  // the registry already groups these under the module name/icon — keep them flat
  menu: [
    { key: 'tax.einvoices', labelKey: 'tax:nav.einvoices', path: '/tax/einvoices', icon: IconFileInvoice, permission: P.einvoice.read },
    { key: 'tax.egs', labelKey: 'tax:nav.egs', path: '/tax/egs', icon: IconCertificate, permission: P.egs.read },
    { key: 'tax.vatReturns', labelKey: 'tax:nav.vatReturns', path: '/tax/vat-returns', icon: IconBuildingBank, permission: P.vatReturn.read },
    { key: 'tax.purchases', labelKey: 'tax:nav.purchases', path: '/tax/purchases', icon: IconShoppingCart, permission: P.purchases.read },
    { key: 'tax.zakat', labelKey: 'tax:nav.zakat', path: '/tax/zakat', icon: IconScale, permission: P.zakat.read },
    { key: 'tax.reports', labelKey: 'tax:nav.reports', path: '/tax/reports', icon: IconReportAnalytics, permission: [P.report.read, P.vatReturn.read, P.einvoice.read, P.zakat.read] },
    { key: 'tax.settings', labelKey: 'tax:nav.settings', path: '/tax/settings', icon: IconSettings, permission: [P.taxCode.read, P.vatReturn.read] },
  ],
  routes: {
    admin: [
      routeGroup('tax/einvoices', 'tax:nav.einvoices', [
        indexRoute(() => einv().then((m) => ({ default: m.EInvoicesPage })), P.einvoice.read),
        lazyRoute(':id', () => einv().then((m) => ({ default: m.EInvoicePage })), { permission: P.einvoice.read, crumb: 'tax:einvoice.detail' }),
      ]),
      routeGroup('tax/egs', 'tax:nav.egs', [
        indexRoute(() => egs().then((m) => ({ default: m.EgsListPage })), P.egs.read),
        lazyRoute(':id', () => egs().then((m) => ({ default: m.EgsWizardPage })), { permission: P.egs.read, crumb: 'tax:egs.detail' }),
      ]),
      routeGroup('tax/vat-returns', 'tax:nav.vatReturns', [
        indexRoute(() => returns().then((m) => ({ default: m.VatReturnsPage })), P.vatReturn.read),
        lazyRoute(':id', () => returns().then((m) => ({ default: m.VatReturnPage })), { permission: P.vatReturn.read, crumb: 'tax:vat.detail' }),
      ]),
      lazyRoute('tax/purchases', () => misc().then((m) => ({ default: m.PurchaseRegisterPage })), { permission: P.purchases.read, crumb: 'tax:nav.purchases' }),
      lazyRoute('tax/settings', () => misc().then((m) => ({ default: m.TaxSettingsPage })), { permission: [P.taxCode.read, P.vatReturn.read], crumb: 'tax:nav.settings' }),
      lazyRoute('tax/reports', () => misc().then((m) => ({ default: m.TaxReportsPage })), { permission: [P.report.read, P.vatReturn.read, P.einvoice.read, P.zakat.read], crumb: 'tax:nav.reports' }),
      routeGroup('tax/zakat', 'tax:nav.zakat', [
        indexRoute(() => zakat().then((m) => ({ default: m.ZakatListPage })), P.zakat.read),
        lazyRoute(':id', () => zakat().then((m) => ({ default: m.ZakatDetailPage })), { permission: P.zakat.read, crumb: 'tax:zakat.detail' }),
      ]),
    ],
  },
  dashboards: [
    { key: 'tax.einvoiceStatus', titleKey: 'tax:widgets.einvoiceStatus', permission: P.einvoice.read, component: EInvoiceStatusWidget, span: { base: 12, md: 6, lg: 4 }, order: 60 },
  ],
});
